export type CryptoAssetAddressKind = "evm" | "solana";

export const CRYPTO_ASSET_ROUTE_CHAINS = [
  { addressKind: "evm", chainId: "8453", slug: "base" },
  { addressKind: "evm", chainId: "1", slug: "ethereum" },
  { addressKind: "evm", chainId: "42161", slug: "arbitrum" },
  { addressKind: "evm", chainId: "56", slug: "bnb" },
  { addressKind: "evm", chainId: "4663", slug: "robinhood" },
  { addressKind: "evm", chainId: "999", slug: "hyperevm" },
  { addressKind: "evm", chainId: "143", slug: "monad" },
  { addressKind: "solana", chainId: "1399811149", slug: "solana" },
] as const satisfies readonly {
  readonly addressKind: CryptoAssetAddressKind;
  readonly chainId: string;
  readonly slug: string;
}[];

export type CryptoAssetRouteChain = (typeof CRYPTO_ASSET_ROUTE_CHAINS)[number];
export type CryptoAssetRouteChainSlug = CryptoAssetRouteChain["slug"];

export interface CryptoAssetRouteParams {
  readonly chain: string;
  readonly tokenAddress: string;
}

export interface ResolvedCryptoAssetRouteParams {
  readonly chainId: string;
  readonly chainSlug: CryptoAssetRouteChainSlug;
  readonly tokenAddress: string;
}

export interface CryptoAssetRouteTarget {
  readonly params: {
    readonly chain: CryptoAssetRouteChainSlug;
    readonly tokenAddress: string;
  };
  readonly search?: undefined;
  readonly to: "/crypto/$chain/$tokenAddress";
}

export interface CryptoAssetRoutePath {
  readonly canonicalPathname: string;
  readonly params: ResolvedCryptoAssetRouteParams;
}

const EVM_TOKEN_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/u;
const SOLANA_TOKEN_ADDRESS_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/u;

export function resolveCryptoAssetRouteChain(
  value: string | number | null | undefined,
): CryptoAssetRouteChain | null {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  return (
    CRYPTO_ASSET_ROUTE_CHAINS.find(
      (chain) => chain.slug === normalized || chain.chainId === normalized,
    ) ?? null
  );
}

export function normalizeCryptoAssetTokenAddress(
  value: string | null | undefined,
  addressKind: CryptoAssetAddressKind,
): string | null {
  const normalized = value?.trim() ?? "";
  if (addressKind === "evm") {
    return EVM_TOKEN_ADDRESS_PATTERN.test(normalized)
      ? normalized.toLowerCase()
      : null;
  }

  return SOLANA_TOKEN_ADDRESS_PATTERN.test(normalized) ? normalized : null;
}

export function parseCryptoAssetRouteParams(
  rawParams: CryptoAssetRouteParams,
): CryptoAssetRouteParams | null {
  const chain = resolveCryptoAssetRouteChain(rawParams.chain);
  if (
    !chain ||
    !normalizeCryptoAssetTokenAddress(rawParams.tokenAddress, chain.addressKind)
  ) {
    return null;
  }

  return {
    chain: rawParams.chain.trim(),
    tokenAddress: rawParams.tokenAddress.trim(),
  };
}

export function resolveCryptoAssetRouteParams(
  params: CryptoAssetRouteParams,
): ResolvedCryptoAssetRouteParams | null {
  const chain = resolveCryptoAssetRouteChain(params.chain);
  if (!chain) {
    return null;
  }

  const tokenAddress = normalizeCryptoAssetTokenAddress(
    params.tokenAddress,
    chain.addressKind,
  );
  if (!tokenAddress) {
    return null;
  }

  return {
    chainId: chain.chainId,
    chainSlug: chain.slug,
    tokenAddress,
  };
}

export function buildCryptoAssetRouteTarget(input: {
  readonly chain: string | number | null | undefined;
  readonly tokenAddress: string | null | undefined;
}): CryptoAssetRouteTarget | null {
  const resolved = resolveCryptoAssetRouteParams({
    chain: String(input.chain ?? ""),
    tokenAddress: input.tokenAddress ?? "",
  });
  if (!resolved) {
    return null;
  }

  return {
    params: {
      chain: resolved.chainSlug,
      tokenAddress: resolved.tokenAddress,
    },
    to: "/crypto/$chain/$tokenAddress",
  };
}

export function buildCryptoAssetHref(input: {
  readonly chain: string | number | null | undefined;
  readonly tokenAddress: string | null | undefined;
}): string | null {
  const target = buildCryptoAssetRouteTarget(input);
  return target
    ? `/crypto/${target.params.chain}/${target.params.tokenAddress}`
    : null;
}

export function readCryptoAssetRoutePathname(
  pathname: string,
): CryptoAssetRoutePath | null {
  const match = /^\/crypto\/([^/]+)\/([^/]+)$/iu.exec(pathname);
  if (!match) {
    return null;
  }

  const params = resolveCryptoAssetRouteParams({
    chain: match[1] ?? "",
    tokenAddress: match[2] ?? "",
  });
  if (!params) {
    return null;
  }

  return {
    canonicalPathname: `/crypto/${params.chainSlug}/${params.tokenAddress}`,
    params,
  };
}
